import { useState, useEffect } from 'react';
import { useT } from '../i18n/useT';
import { useLanguage } from '../i18n/LanguageContext';
import { header as headerEn } from '../i18n/translations/en';
import { header as headerEs } from '../i18n/translations/es';
import ContactModal from './ContactModal';
import './Header.css';

const Header = () => {
    const [scrolled, setScrolled]         = useState(false);
    const [openMenu, setOpenMenu]         = useState(null);
    const [mobileOpen, setMobileOpen]     = useState(false);
    const [mobileExpanded, setMobileExpanded] = useState(null);
    const [contactOpen, setContactOpen]   = useState(false);
    const { lang, toggle } = useLanguage();
    const t = useT({ en: headerEn, es: headerEs });

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!contactOpen) {
            document.body.style.overflow = mobileOpen ? 'hidden' : '';
        }
    }, [mobileOpen, contactOpen]);

    const closeAll = () => {
        setOpenMenu(null);
        setMobileOpen(false);
        setMobileExpanded(null);
    };

    const openContact = () => {
        closeAll();
        setContactOpen(true);
    };

    const toggleMobileAccordion = (id) => {
        setMobileExpanded(prev => prev === id ? null : id);
    };

    return (
        <>
            <header className={`header ${scrolled ? 'scrolled solid-header' : 'transparent-header'}`}>
                <div className="container header-container">
                    <a href="/" className="logo-link" onClick={closeAll}>
                        <img src="/assets/images/vscdc-seal.png" alt="VSCDC Seal" className="logo-seal" />
                        <div className="logo">Venus<span>EDC</span></div>
                    </a>

                    {/* Desktop nav */}
                    <nav className="desktop-nav">
                        <ul>
                            {t.navItems.map(item => (
                                <li
                                    key={item.id}
                                    className="dropdown"
                                    onMouseEnter={() => setOpenMenu(item.id)}
                                    onMouseLeave={() => setOpenMenu(null)}
                                >
                                    <a href={item.href} onClick={closeAll}>
                                        {item.label} <span className="caret">▼</span>
                                    </a>
                                    {openMenu === item.id && (
                                        <ul className="dropdown-menu">
                                            {item.children.map(child => (
                                                <li key={child.href}>
                                                    <a href={child.href} onClick={closeAll}>{child.label}</a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className="header-right">
                        <button className="btn-lang" onClick={toggle} aria-label="Switch language">
                            {lang === 'en' ? 'ES' : 'EN'}
                        </button>
                        <button className="btn btn-primary desktop-cta" onClick={openContact}>
                            {t.contactUs}
                        </button>
                        <button
                            className={`hamburger ${mobileOpen ? 'open' : ''}`}
                            onClick={() => setMobileOpen(v => !v)}
                            aria-label={t.ariaMenu}
                        >
                            <span></span><span></span><span></span>
                        </button>
                    </div>
                </div>
            </header>

            {mobileOpen && <div className="mobile-overlay" onClick={closeAll} />}

            <nav className={`mobile-drawer ${mobileOpen ? 'mobile-drawer--open' : ''}`}>
                <div className="mobile-drawer-header">
                    <a href="/" className="logo-link" onClick={closeAll}>
                        <img src="/assets/images/vscdc-seal.png" alt="VSCDC Seal" className="logo-seal" />
                        <div className="logo">Venus<span>EDC</span></div>
                    </a>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <button className="btn-lang" onClick={toggle}>{lang === 'en' ? 'ES' : 'EN'}</button>
                        <button className="mobile-close" onClick={closeAll} aria-label="Close menu">✕</button>
                    </div>
                </div>

                <ul className="mobile-nav-list">
                    {t.navItems.map(item => (
                        <li key={item.id} className="mobile-nav-item">
                            <button
                                className={`mobile-nav-parent ${mobileExpanded === item.id ? 'expanded' : ''}`}
                                onClick={() => toggleMobileAccordion(item.id)}
                            >
                                {item.label}
                                <span className="mobile-caret">▼</span>
                            </button>
                            {mobileExpanded === item.id && (
                                <ul className="mobile-submenu">
                                    <li>
                                        <a href={item.href} onClick={closeAll} className="mobile-submenu-overview">
                                            {t.viewAll} {item.label}
                                        </a>
                                    </li>
                                    {item.children.map(child => (
                                        <li key={child.href}>
                                            <a href={child.href} onClick={closeAll}>{child.label}</a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>

                <div className="mobile-drawer-footer">
                    <button className="btn btn-primary" style={{ width: '100%' }} onClick={openContact}>
                        {t.contactUs}
                    </button>
                </div>
            </nav>

            <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
        </>
    );
};

export default Header;
